// Copyright (c) StrangeBee 2023

import React, { ReactElement, useContext, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { handleNotificationError, openMessage, useLocalMessage } from '@Helpers/';
import {
    CaseOutput,
    CaseResolutionStatus,
    CaseStatus,
    RelatedCaseOutput,
    ObjectTypeV1,
    CustomStatusMapType,
    CaseStatusStage,
    QueryParams,
    PermissionsEnum,
} from 'thehive-sdk';
import RouteBuilder from '@Services/RouteBuilder';
import { RouteType } from '@Types';
import { THLayout } from '@Molecules/THLayout';
import { THList } from '@Molecules/THList';
import { FormatDate } from '@Molecules/Format';
import { Badge } from 'antd';
import QuickFilterList from '@Molecules/QuickFilterList/QuickFilterList';
import CaseListTitle from '@Organisms/Case/CaseListTitle/CaseListTitle';
import useConfigList from '@Hooks/useConfigList';
import client from '@Client';
import { StorageFilterEnum } from '@Hooks/useStoragePage';
import { DisplayEmptyData } from '@Atoms/Display/DisplayEmptyData/DisplayEmptyData';
import { formatObservableTitle } from '@Helpers/observable';
import { ICONS } from '@Constants/iconsApp';
import { useDrawer } from '@Hooks/useDrawer';
import { CaseDrawer } from '@Organisms/Case/CaseDrawer/CaseDrawer';
import { computeEventList } from '@Organisms/Case/eventList';
import { ScopeContext } from '@Context/ScopeProvider';
import { actionList } from '@Organisms/Case/actionList';
import IconCount from '@Atoms/IconCount/IconCount';
import { useQuery } from 'react-query';
import { useCurrentUser } from '@Queries/userQueries';
import { useSplitPermissions } from '@Hooks/useSplitPermissions';

import './RelatedCasesList.scss';

const RelatedCasesList = (): ReactElement => {
    const i = useLocalMessage();

    const { data: currentUser } = useCurrentUser();
    const { organisation: userOrganisation = '', permissions = [] } = currentUser || {};
    const [stateFilter, setStateFilter] = useState<CaseResolutionStatus | CaseStatus>();

    const { caseId } = useParams<{ caseId: string }>();
    const { visible, setVisible, data, setData } = useDrawer<CaseOutput>({});
    const [caseStatus, setCaseStatus] = React.useState<CustomStatusMapType>({});

    const hasPermissions = useSplitPermissions(permissions, [
        PermissionsEnum.MANAGE_ACTION,
        PermissionsEnum.MANAGE_CASE_UPDATE,
        PermissionsEnum.MANAGE_CASE_DELETE,
    ]);

    const {
        customStatus: {
            case: { list: customStatusList },
        },
    } = useContext(ScopeContext);

    const config = useConfigList<RelatedCaseOutput>({
        controller: client.CaseController,
        selector: {
            head: [{ name: 'getCase', idOrName: caseId }, { name: 'linkedCases' }],
            name: 'get-case-links',
        },
        selectorCustomCount: {
            head: [{ name: 'getCase', idOrName: caseId }, { name: 'linkedCaseCount' }],
            name: 'count-get-case-links',
        },
        objectType: ObjectTypeV1.CASE,
        storageId: StorageFilterEnum.CASE_RELATED,
        pagination: 'off',
    });

    const { dataSource = [], columnsRenderer, status = 'loading', listRefetch, countRefetch } = config;

    const eventList = useMemo(
        () =>
            computeEventList({
                controller: client.CaseController,
                locale: i,
                onError: handleNotificationError,
                onSuccess: (count, message) => {
                    if (count) {
                        countRefetch();
                    }
                    if (message) {
                        openMessage('success', message);
                    }
                    listRefetch();
                },
            }),
        [listRefetch, countRefetch, i],
    );

    const caseActionList = actionList({
        local: i,
        caseStatus,
        eventList,
        customStatusList,
        organisation: userOrganisation,
        onCloseBulkStatusManage: () => {
            listRefetch();
        },
        onEditBulkReopen: () => {
            listRefetch();
        },
        onDeleteBulk: () => {
            listRefetch();
            countRefetch();
        },
        hasPermissions,
        currentUserData: currentUser,
    });

    const quickFilterList = [
        {
            label: i('filter.quick.related-case.all'),
            callback: (): void => setStateFilter(undefined),
        },
        {
            label: i('filter.quick.related-case.open'),
            callback: (): void => setStateFilter(CaseStatus.OPEN),
        },
        {
            label: i('filter.quick.related-case.indeterminate'),
            callback: (): void => setStateFilter(CaseResolutionStatus.INDETERMINATE),
        },
        {
            label: i('filter.quick.related-case.false-positive'),
            callback: (): void => setStateFilter(CaseResolutionStatus.FALSE_POSITIVE),
        },
        {
            label: i('filter.quick.related-case.true-positive'),
            callback: (): void => setStateFilter(CaseResolutionStatus.TRUE_POSITIVE),
        },
        {
            label: i('filter.quick.related-case.other'),
            callback: (): void => setStateFilter(CaseResolutionStatus.OTHER),
        },
    ];

    const columns = columnsRenderer([
        {
            title: i('related.list.table.columns.title'),
            className: 'related-cases-list__column__name column_preview',
            render: (caseData): ReactElement => (
                <CaseListTitle
                    data={caseData as CaseOutput}
                    customFieldsIsOpen
                    tagsIsOpen
                    onPreviewClick={() => {
                        caseData._id &&
                            eventList.onGetDetails(caseData._id).then((data) => {
                                setData(data);
                                setVisible(true);
                            });
                    }}
                />
            ),
        },
        {
            title: i('related.list.table.columns.date'),
            className: 'related-cases-list__column__date',
            render: ({ startDate }): ReactElement => <FormatDate date={startDate} />,
        },
        {
            title: `#${i('common.observables')}`,
            className: 'related-cases-list__column__count',
            render: ({ linksCount = 0 }): ReactElement => <Badge className="badge" count={linksCount} />,
        },
        {
            title: i('related.list.table.columns.link'),
            className: 'related-cases-list__column__observable',
            render: ({ linkedWith, linksCount = 0, _id }): ReactElement => {
                const list = linkedWith.reduce((acc, { _id, dataType, data, attachment }, index) => {
                    const title = formatObservableTitle({ attachment, data });
                    if (index > 2) {
                        return acc;
                    }
                    return [
                        ...acc,
                        <Link key={_id} to={RouteBuilder.compute('cases.observable', { caseId, observableId: _id })}>
                            <b>[{dataType}]: </b>
                            {title}
                        </Link>,
                    ];
                }, [] as ReactElement[]);

                return (
                    <div className="related-cases-list__column__observable__list">
                        {list}

                        {list.length !== linksCount && (
                            <Link
                                className="related-cases-list__column__observable__list__show"
                                to={RouteBuilder.compute('cases.observables', { caseId: _id })}
                            >
                                {i('general.show-more')}
                            </Link>
                        )}
                    </div>
                );
            },
        },
    ]);

    const filteredData = useMemo(
        () =>
            dataSource.filter(
                ({ resolutionStatus, status }) =>
                    !stateFilter || resolutionStatus === stateFilter || status === stateFilter,
            ),
        [dataSource, stateFilter],
    );

    useEffect(() => {
        if (customStatusList.length) {
            const caseStatusMap = customStatusList.reduce((acc, curr) => {
                if (![CaseStatusStage.NEW, CaseStatusStage.IN_PROGRESS].includes(curr.stage as CaseStatusStage)) {
                    return acc;
                }
                if (!acc[curr.stage]) {
                    acc[curr.stage] = [];
                }
                acc[curr.stage].push(curr);
                return acc;
            }, {} as CustomStatusMapType);
            setCaseStatus(caseStatusMap);
        }
    }, [customStatusList]);

    useDrawer.watch(dataSource, () => {
        if (data && visible) {
            eventList.onGetDetails(data._id).then((data) => {
                setData(data);
            });
        }
    });
    return (
        <THLayout>
            <>
                <THList>
                    <THList.Header>
                        <THList.Slot name="MANAGE">
                            <QuickFilterList quickFilterList={quickFilterList} />
                        </THList.Slot>
                    </THList.Header>
                    <THList.Content
                        dataSource={filteredData}
                        columns={columns}
                        rowKey="_id"
                        emptyPlaceholder={
                            <DisplayEmptyData spin={status === 'loading'} label={i('case.related-cases.none')} />
                        }
                    />
                </THList>
                {data && visible && (
                    <CaseDrawer
                        data={data}
                        eventList={eventList}
                        actionList={caseActionList}
                        visible={visible}
                        afterVisible={setVisible}
                        onEditCounter={() => {
                            listRefetch();
                        }}
                    />
                )}
            </>
        </THLayout>
    );
};

const MenuIcon = (): ReactElement => {
    const { caseId } = useParams<{ caseId: string }>();

    const queryParams = {
        selector: {
            head: [{ name: 'getCase', idOrName: caseId }, { name: 'linkedCaseCount' }],
            name: 'count-get-case-links',
        },
    };

    const query = useQuery(
        'count-linked-cases',
        () => client.CaseController.custom<number>(queryParams as QueryParams),
        {
            refetchInterval: false,
            refetchOnWindowFocus: false,
        },
    );

    const { data: count = 0, refetch } = query;

    useEffect(() => {
        refetch();
    }, [refetch]);

    return count ? (
        <IconCount count={count}>
            <ICONS.CaseIconOutlined />
        </IconCount>
    ) : (
        <ICONS.CaseIconOutlined />
    );
};

export const Route: RouteType = {
    menu: {
        label: 'case.layout.nav-bar.related-cases',
        icon: <ICONS.CaseIconOutlined />,
        shortcut: {
            enable: true,
            render: <MenuIcon />,
        },
    },
    path: RouteBuilder.compute('cases.related-cases'),
    content: <RelatedCasesList />,
    space: ['user'],
    breadcrumb: { label: 'breadcrumb.case.related-cases' },
};
